import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';

import { TenancyService } from '../../core/tenancy/tenancy.service';
import { OrganizationService } from '../../core/tenancy/organization.service';
import { VenueSwitcherService } from '../../core/tenancy/venue-switcher.service';
import { NvMenu, NvMenuItem, NvMenuTrigger } from '../../shared/ui/menu/nv-menu';
import { NvIcon } from '../../shared/ui/icon/nv-icon';

/**
 * The rail's venue dropdown — invisible for every single-venue customer
 * (the overwhelming majority today), and for a multi-venue org member with
 * only one active venue left. Only appears once there is genuinely
 * something to switch between, per the architecture plan's "a
 * single-location customer sees no organization UI at all" principle.
 */
@Component({
  selector: 'nv-venue-switcher',
  imports: [NvMenu, NvMenuItem, NvMenuTrigger, NvIcon],
  templateUrl: './nv-venue-switcher.html',
  styleUrl: './nv-venue-switcher.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NvVenueSwitcher {
  private readonly tenancy = inject(TenancyService);
  private readonly organizations = inject(OrganizationService);
  private readonly switcher = inject(VenueSwitcherService);

  /** Every venue of the caller's own organization, re-queried whenever orgId changes (a rare event — sign-in, or removeOrgMember). [] for a non-org member, exactly the "invisible by default" case. */
  private readonly orgVenues = toSignal(
    toObservable(this.tenancy.orgId).pipe(
      switchMap((orgId) => (orgId ? this.organizations.listVenues(orgId) : of([])))
    ),
    { initialValue: [] }
  );

  protected readonly items = computed(() =>
    this.orgVenues()
      .filter((v) => v.status === 'active')
      .map((v) => ({ id: v.id, name: v.profile.name }))
  );

  /** Nothing to switch between (single venue, or no org at all) — the whole component renders nothing. */
  protected readonly visible = computed(
    () => this.tenancy.isOrgMember() && this.items().length > 1
  );

  protected readonly activeVenueId = this.tenancy.licenseId;

  async select(venueId: string): Promise<void> {
    await this.switcher.switchTo(venueId);
  }
}
